import React, { useState, useEffect } from 'react';
import { Search, Filter, Globe, Smartphone, Wifi, Database, Clock, Star, ShoppingCart, User, CreditCard, ArrowLeft, MapPin, X } from 'lucide-react';
import { db } from './firebase';
import { collection, onSnapshot, query, orderBy, limit, startAfter, getDocs } from 'firebase/firestore';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from "./useTranslation";
  
const Browse = ({ user, setShowAuthModal }) => {
  const { t } = useTranslation(); 
  const [countries, setCountries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('name');
  const [filterDuration, setFilterDuration] = useState('all');
  const [showPlanDetailsModal, setShowPlanDetailsModal] = useState(false);
  const [planDetailsModalData, setPlanDetailsModalData] = useState(null);
  const [showAllPlans, setShowAllPlans] = useState(false);
  const [error, setError] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);


  // Pagination states
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [allCountriesLoaded, setAllCountriesLoaded] = useState(false);

  const COUNTRIES_PER_PAGE = 9;
  const LOAD_MORE_COUNT = 6;



  // Add custom styles for text shadow
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .shadow-text {
        text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Initial load of countries (first 9)
  useEffect(() => {
    loadInitialCountries();
  }, []);

  const loadInitialCountries = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'countries'), 
        orderBy('name'),
        limit(COUNTRIES_PER_PAGE)
      );
      
      const snapshot = await getDocs(q);
      const countriesData = snapshot.docs.map(doc => ({
        iso: doc.id,
        ...doc.data()
      }));
      
      setCountries(countriesData);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(snapshot.docs.length === COUNTRIES_PER_PAGE);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching initial countries:', error);
      setError('Failed to load countries from Firebase');
      setLoading(false);
    }
  };

  const loadMoreCountries = async () => {
    if (!hasMore || loadingMore || !lastDoc) return;

    try {
      setLoadingMore(true);
      const q = query(
        collection(db, 'countries'),
        orderBy('name'),
        startAfter(lastDoc),
        limit(LOAD_MORE_COUNT)
      );

      const snapshot = await getDocs(q);
      const newCountries = snapshot.docs.map(doc => ({
        iso: doc.id,
        ...doc.data()
      }));

      if (newCountries.length > 0) {
        setCountries(prev => [...prev, ...newCountries]);
        setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
        setHasMore(snapshot.docs.length === LOAD_MORE_COUNT);
      } else {
        setHasMore(false);
        setAllCountriesLoaded(true);
      }
      
      setLoadingMore(false);
    } catch (error) {
      console.error('Error loading more countries:', error);
      setLoadingMore(false);
    }
  };

const searchCountriesInFirebase = async (searchTerm) => {
  if (!searchTerm.trim()) {
    setSearchResults([]);
    setIsSearching(false);
    return;
  }

  try {
    setIsSearching(true);
    
    // Search by country name (case insensitive)
    const nameQuery = query(
      collection(db, 'countries'),
      orderBy('name')
    );
    
    const snapshot = await getDocs(nameQuery);
    const allCountries = snapshot.docs.map(doc => ({
      iso: doc.id,
      ...doc.data()
    }));

    // Filter results client-side for more flexible search
    const filtered = allCountries.filter(country =>
      country.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.iso?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.region?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    setSearchResults(filtered);
    setIsSearching(false);
  } catch (error) {
    console.error('Error searching countries:', error);
    setIsSearching(false);
  }
};

useEffect(() => {
  const timeoutId = setTimeout(() => {
    if (searchTerm.length > 0) {
      searchCountriesInFirebase(searchTerm);
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }
  }, 300); // 300ms debounce

  return () => clearTimeout(timeoutId);
}, [searchTerm]);

// Update the filteredCountries logic to use search results when searching
const getDisplayCountries = () => {
  if (searchTerm.length > 0) {
    return searchResults; // Use search results from Firebase
  }
  return countries; // Use paginated results when not searching
};

const displayCountries = getDisplayCountries();

  // Auto-select first plan when country is selected
  useEffect(() => {
    if (selectedCountry) {
      const allPlans = getCountryPlans(selectedCountry);
      const filteredPlans = filterPlansByDuration(allPlans);
      const sortedPlans = sortPlans(filteredPlans);
      if (sortedPlans.length > 0) {
        setSelectedPlan(sortedPlans[0]);
      }
    }
  }, [selectedCountry, filterDuration, sortBy]);

  // Filter countries based on search term
  const filteredCountries = countries.filter(country =>
    country.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    country.iso?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    country.region?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get all plans from selected country with pricing
  const getCountryPlans = (country) => {
    if (!country) return [];
    
    const plans = [];
    const markup = country.markup_percentage || 25;

    // Add fixed plans
    Object.entries(country.fixed_plans || {}).forEach(([duration, durationPlans]) => {
      durationPlans.forEach(plan => {
        const finalPrice = (plan.price * (1 + markup / 100));
        plans.push({
          ...plan,
          country: country.name,
          countryCode: country.iso,
          duration: parseInt(duration),
          finalPrice: finalPrice,
          isUnlimited: false,
          type: 'fixed',
          bundle_name: plan.name,
          country_code: country.iso,
          data_amount: plan.data_amount,
          auto_start: plan.autostart
        });
      });
    });

    // Add unlimited plans
    Object.entries(country.unlimited_plans || {}).forEach(([duration, durationPlans]) => {
      durationPlans.forEach(plan => {
        const finalPrice = (plan.price * (1 + markup / 100));
        plans.push({
          ...plan,
          country: country.name,
          countryCode: country.iso,
          duration: parseInt(duration),
          finalPrice: finalPrice,
          isUnlimited: true,
          type: 'unlimited',
          bundle_name: plan.name,
          country_code: country.iso,
          data_amount: -1, // Unlimited
          auto_start: plan.autostart
        });
      });
    });

    return plans;
  };

  // Filter plans by duration
  const filterPlansByDuration = (plans) => {
    if (filterDuration === 'all') return plans;
    return plans.filter(plan => plan.duration === parseInt(filterDuration));
  };

  // Sort plans with custom priority order
  const sortPlans = (plans) => {
    return [...plans].sort((a, b) => {
      // Custom priority order
      const getPriority = (plan) => {
        if (plan.isUnlimited && plan.duration === 1) return 1; // 1 Day Unlimited
        if (plan.isUnlimited && plan.duration === 3) return 2; // 3 Days Unlimited
        if (plan.isUnlimited && plan.duration === 5) return 3; // 5 Days Unlimited
        if (!plan.isUnlimited && plan.duration === 7) return 4; // 7 Days Fixed Data
        
        // For other plans, sort by type first (unlimited before fixed), then by duration
        if (plan.isUnlimited && !a.isUnlimited) return -1;
        if (!plan.isUnlimited && a.isUnlimited) return 1;
        
        return plan.duration - a.duration; // Sort by duration for remaining plans
      };

      const priorityA = getPriority(a);
      const priorityB = getPriority(b);
      
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // If same priority, use the original sorting logic
      switch (sortBy) {
        case 'price':
          return a.finalPrice - b.finalPrice;
        case 'data':
          if (a.isUnlimited && !b.isUnlimited) return -1;
          if (!a.isUnlimited && b.isUnlimited) return 1;
          if (a.isUnlimited && b.isUnlimited) return 0;
          return b.data_amount - a.data_amount;
        case 'duration':
          return a.duration - b.duration;
        default: // name
          return a.name?.localeCompare(b.name) || 0;
      }
    });
  };

  // Handle plan purchase - navigate to checkout
  const handlePurchase = (plan) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    
    // Navigate to checkout page with plan data
    window.location.href = `/checkout?plan=${encodeURIComponent(JSON.stringify({
      id: plan.bundle_name || plan.name,
      country: selectedCountry.name,
      countryCode: selectedCountry.iso,
      countryFlag: selectedCountry.image_url,
      dataAmount: plan.isUnlimited ? 'Unlimited' : `${(plan.data_amount / 1000).toFixed(1)} GB`,
      duration: plan.duration,
      price: plan.finalPrice.toFixed(2),
      planData: plan
    }))}`;
  };

  // Handle plan details modal
  const handlePlanDetails = (plan, event) => {
    event.stopPropagation();
    setPlanDetailsModalData(plan);
    setShowPlanDetailsModal(true);
  };

  // Handle plan selection
  const handlePlanSelection = (plan) => {
    setSelectedPlan(plan);
  };

  const CountryCard = ({ country }) => {
    // Get 1-day unlimited plan only (like home page)
    const getUnlimitedOneDayPlan = () => {
      if (country.unlimited_plans && country.unlimited_plans['1']) {
        const plans = country.unlimited_plans['1'];
        if (plans && plans.length > 0) {
          const markup = country.markup_percentage || 25;
          const plan = plans[0];
          return {
            ...plan,
            duration: 1,
            type: 'unlimited',
            finalPrice: plan.price * (1 + markup / 100)
          };
        }
      }
      return null;
    };

    const unlimitedPlan = getUnlimitedOneDayPlan();
    const hasPlans = unlimitedPlan !== null;

    return (
      <div
        onClick={() => setSelectedCountry(country)}
        className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow cursor-pointer group"
      >
        {/* Banner Image */}
        <div className="h-32 bg-gradient-to-r from-blue-400 to-purple-500 relative overflow-hidden">
          {country.image_url ? (
            <img 
              src={country.image_url} 
              alt={country.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback to gradient if image fails to load
                e.target.style.display = 'none';
              }}
            />
          ) : null}
          {/* Country name overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-30 flex items-end">
            <div className="p-4">
              <h3 className="text-xl font-bold text-white shadow-text">{country.name}</h3>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {hasPlans ? (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <Wifi className="w-5 h-5 text-purple-500 mr-2" />
                  <span className="font-semibold text-gray-900">
                    Unlimited
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-green-600">
                    ${unlimitedPlan.finalPrice.toFixed(2)}
                  </div>
                </div>
              </div>
              <div className="flex items-center text-sm text-gray-500 mb-3">
                <Clock className="w-4 h-4 mr-1" />
                1 day
              </div>
              <p className="text-sm text-gray-600">
                Unlimited data starting from <span className="font-semibold text-green-600">${unlimitedPlan.finalPrice.toFixed(2)}</span>
              </p>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-6">
              <Wifi className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No unlimited plans available</p>
            </div>
          )}
          
        <button
          onClick={(e) => {
            e.stopPropagation();
            setSelectedCountry(country);
          }}
          disabled={!hasPlans}
          className="w-full bg-[#7B3EF0] hover:bg-[#6b33e6] disabled:bg-gray-400 disabled:cursor-not-allowed text-white py-3 px-4 rounded-lg font-medium transition-colors"
        >
          {hasPlans ? 'View All Plans' : 'No Plans Available'}
        </button>

        </div>
      </div>
    );
  };

// Updated PlanDetailsModal with translations
const PlanDetailsModal = () => {
  const { t } = useTranslation();
  
  if (!planDetailsModalData) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900">
            {t('browse.planDetails.title')}
          </h3>
          <button
            onClick={() => setShowPlanDetailsModal(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          {/* Plan Type Header */}
          <div className={`p-4 rounded-lg ${planDetailsModalData.isUnlimited ? 'bg-gradient-to-r from-purple-500 to-purple-600' : 'bg-gradient-to-r from-blue-500 to-blue-600'} text-white`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {planDetailsModalData.isUnlimited ? (
                  <Wifi className="w-5 h-5 mr-2" />
                ) : (
                  <Database className="w-5 h-5 mr-2" />
                )}
                <span className="font-bold text-lg">
                  {planDetailsModalData.isUnlimited ? t('browse.planDetails.unlimitedData') : `${(planDetailsModalData.data_amount / 1000).toFixed(1)}GB ${t('browse.planDetails.data')}`}
                </span>
              </div>
              <div className="text-right">
                <div className="text-xl font-bold">
                  ${planDetailsModalData.finalPrice.toFixed(2)}
                </div>
                <div className="text-xs opacity-90">USD</div>
              </div>
            </div>
          </div>

          {/* Plan Information */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">{t('browse.planDetails.country')}:</span>
              <span className="font-semibold text-gray-900">{planDetailsModalData.country}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('browse.planDetails.duration')}:</span>
              <span className="font-semibold text-gray-900">
                {planDetailsModalData.duration} {planDetailsModalData.duration === 1 ? t('browse.planDetails.day') : t('browse.planDetails.days')}
              </span>
            </div>
            {planDetailsModalData.speed && planDetailsModalData.speed.length > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">{t('browse.planDetails.network')}:</span>
                <span className="font-semibold text-gray-900">
                  {planDetailsModalData.speed.join(', ')}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">{t('browse.planDetails.autoStart')}:</span>
              <span className={`font-semibold ${planDetailsModalData.auto_start ? 'text-green-600' : 'text-red-600'}`}>
                {planDetailsModalData.auto_start ? t('browse.planDetails.enabled') : t('browse.planDetails.disabled')}
              </span>
            </div>
            {planDetailsModalData.groups && planDetailsModalData.groups.length > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">{t('browse.planDetails.planType')}:</span>
                <span className="font-semibold text-gray-900 text-xs">
                  {planDetailsModalData.groups.join(', ')}
                </span>
              </div>
            )}
          </div>

          {/* Description */}
          {planDetailsModalData.description && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">{t('browse.planDetails.description')}</h4>
              <p className="text-sm text-blue-800">{planDetailsModalData.description}</p>
            </div>
          )}

          {/* Purchase Button */}
          <button
            onClick={() => {
              setShowPlanDetailsModal(false);
              handlePurchase(planDetailsModalData);
            }}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 px-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            {user ? t('browse.planDetails.purchaseThisPlan') : t('browse.planDetails.signInToPurchase')}
          </button>
        </div>
      </div>
    </div>
  );
};

  if (loading && countries.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading eSIM plans...</p>
        </div>
      </div>
    );
  }

return (
  <div className="min-h-screen bg-gray-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {!selectedCountry ? (
        // Countries Grid View
        <>
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {t('browse.main.title')}
            </h1>
            <p className="text-lg text-gray-600">
              {t('browse.main.subtitle')}
            </p>
          </div>

          {/* Search and Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative group">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors duration-200" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('browse.search.placeholder')}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg 
                           focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                           hover:border-gray-400
                           bg-white text-gray-900 
                           placeholder:text-gray-500
                           transition-all duration-200 ease-in-out
                           shadow-sm hover:shadow-md focus:shadow-lg"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 
                             text-gray-400 hover:text-gray-600 
                             transition-colors duration-200 p-1 rounded-full 
                             hover:bg-gray-100"
                    aria-label={t('browse.search.clearSearch')}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            
            {error && (
              <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                {error}
              </div>
            )}
          </div>

          {/* Countries Grid */}
          {displayCountries.length === 0 && !loading && !isSearching ? (
            <div className="text-center py-12">
              <Globe className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">
                {searchTerm ? t('browse.search.noCountriesFound') : t('browse.search.noCountriesAvailable')}
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-blue-600 hover:underline"
                >
                  {t('browse.search.clearSearchButton')}
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Show searching indicator */}
              {isSearching && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">{t('browse.search.searching')}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {displayCountries.map(country => (
                  <CountryCard key={country.iso} country={country} />
                ))}
              </div>

              {/* Show More Countries Button - Only show when NOT searching */}
              {!searchTerm && hasMore && !isSearching && (
                <div className="text-center mt-8">
                  <button
                    onClick={loadMoreCountries}
                    disabled={loadingMore}
                    className="inline-flex items-center px-6 py-3 bg-[#7B3EF0] hover:bg-[#6b33e6] disabled:bg-purple-400 text-white rounded-lg font-medium transition-colors"
                  >
                    {loadingMore ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {t('browse.loading.loadingText')}
                      </>
                    ) : (
                      <>
                        {t('browse.buttons.showMoreCountries')}
                        <ChevronDown className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* All Countries Loaded Message - Only show when NOT searching */}
              {!searchTerm && !hasMore && allCountriesLoaded && countries.length > COUNTRIES_PER_PAGE && !isSearching && (
                <div className="text-center mt-8">
                  <p className="text-gray-500">
                    {t('browse.loading.allCountriesLoaded')} ({countries.length} {t('browse.loading.total')})
                  </p>
                </div>
              )}

              {/* Search Results Count */}
              {searchTerm && !isSearching && (
                <div className="text-center mt-8">
                  <p className="text-gray-500">
                    {displayCountries.length} {displayCountries.length === 1 ? t('browse.search.country') : t('browse.search.countries')} {t('browse.search.foundFor')} "{searchTerm}"
                  </p>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        // Selected Country Plans View - Same as Dashboard
        <>
          {/* Back Button */}
          <div className="mb-4 md:mb-6">
            <button
              onClick={() => setSelectedCountry(null)}
              className="flex items-center text-[#7B3EF0] hover:underline"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              {t('browse.navigation.backToCountries')}
            </button>
          </div>

          {/* Main Content Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8 mb-4 md:mb-8">
            {/* Left Side - Country Image */}
            <div className="relative">
              <h1 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">{selectedCountry.name}</h1>
              <div className="aspect-[4/3] rounded-xl md:rounded-2xl overflow-hidden shadow-lg md:shadow-2xl">
                {selectedCountry.image_url ? (
                  <img 
                    src={selectedCountry.image_url} 
                    alt={selectedCountry.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-blue-400 via-purple-500 to-pink-500 flex items-center justify-center">
                    <Globe className="w-16 md:w-24 h-16 md:h-24 text-white opacity-80" />
                  </div>
                )}
              </div> 
              
              {/* Country Description - Moved below image */}
              <div className="mt-4 md:mt-6">
                <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                {t('browse.countryDetails.description').replace('{{country}}', selectedCountry?.name ?? '')}
              </p>

              </div>
            </div>

            {/* Right Side - Plans and Info */}
            <div className="space-y-4 md:space-y-6">
              {/* Plans List */}
              <div className="space-y-3 md:space-y-4">
                {(() => {
                  const allPlans = getCountryPlans(selectedCountry);
                  const filteredPlans = filterPlansByDuration(allPlans);
                  const sortedPlans = sortPlans(filteredPlans);
                  
                  if (sortedPlans.length === 0) {
                    return (
                      <div className="text-center py-6 md:py-8">
                        <Smartphone className="w-10 md:w-12 h-10 md:h-12 text-gray-300 mx-auto mb-3 md:mb-4" />
                        <p className="text-sm md:text-base text-gray-500">
                          {t('browse.plans.noPlansAvailable')}
                        </p>
                      </div>
                    );
                  }

                  // Determine how many plans to show
                  const plansToShow = showAllPlans ? sortedPlans : sortedPlans.slice(0, 6);

                  return plansToShow.map((plan, index) => (
                    <div 
                      key={`${plan.bundle_name || plan.name}-${plan.duration}-${index}`}
                      className={`flex items-center justify-between p-3 md:p-4 rounded-lg md:rounded-xl border transition-all duration-300 cursor-pointer ${
                        selectedPlan && selectedPlan.bundle_name === plan.bundle_name && selectedPlan.duration === plan.duration
                          ? 'border-blue-500 bg-blue-50 shadow-md' 
                          : 'border-gray-200 hover:border-blue-300 bg-white hover:shadow-sm'
                      }`}
                      onClick={() => handlePlanSelection(plan)}
                    >
                      <div className="flex items-center space-x-2 md:space-x-4 flex-1 min-w-0">
                        <button 
                          onClick={(e) => handlePlanDetails(plan, e)}
                          className="px-2 md:px-3 py-1 md:py-1.5 bg-blue-100 text-black rounded-2xl md:rounded-3xl text-xs font-medium hover:bg-blue-200 transition-colors whitespace-nowrap"
                        >
                          <span className="hidden sm:inline">{t('browse.plans.planDetails')}</span>
                          <span className="sm:hidden">{t('browse.plans.details')}</span>
                        </button>
                        <div className="flex items-center space-x-2 md:space-x-3 min-w-0">
                          {/* Network Type Badge */}
                          <div className="flex items-center justify-center w-8 md:w-10 h-5 md:h-6 bg-gray-100 rounded">
                            <span className="text-xs font-bold text-black">5G</span>
                          </div>
                          <div className="min-w-0">
                            <div className="text-base md:text-lg font-bold text-gray-900 truncate">
                              {plan.isUnlimited ? t('browse.plans.unlimited') : `${(plan.data_amount / 1000).toFixed(1)} GB`}
                            </div>
                            <div className="text-xs md:text-sm text-gray-600 truncate">
                              {t('browse.plans.for')} {plan.duration} {plan.duration === 1 ? t('browse.plans.day').toUpperCase() : t('browse.plans.days').toUpperCase()}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
                        <div className="text-right">
                          <div className="text-lg md:text-xl font-bold text-orange-500">
                            <span className='text-xs md:text-sm text-black mr-1 md:mr-2'>USD</span> {plan.finalPrice.toFixed(2)}
                          </div>
                        </div>
                        <div className={`w-5 md:w-6 h-5 md:h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          selectedPlan && selectedPlan.bundle_name === plan.bundle_name && selectedPlan.duration === plan.duration
                            ? 'border-blue-500 bg-blue-500' 
                            : 'border-gray-300'
                        }`}>
                          {selectedPlan && selectedPlan.bundle_name === plan.bundle_name && selectedPlan.duration === plan.duration && (
                            <div className="w-1.5 md:w-2 h-1.5 md:h-2 bg-white rounded-full"></div>
                          )}
                        </div>
                      </div>
                    </div>
                  ));
                })()}
              </div>

              {/* Show More/Less Plans Button */}
              {(() => {
                const allPlans = getCountryPlans(selectedCountry);
                const filteredPlans = filterPlansByDuration(allPlans);
                
                if (filteredPlans.length > 6) {
                  return (
                    <div className="text-center">
                      <button
                        onClick={() => setShowAllPlans(!showAllPlans)}
                        className="inline-flex items-center px-4 md:px-6 py-2 md:py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors text-sm md:text-base"
                      >
                        {showAllPlans ? (
                          <>
                            <span className="hidden sm:inline">{t('browse.buttons.showLessPlans')}</span>
                            <span className="sm:hidden">{t('browse.buttons.showLess')}</span>
                            <ChevronUp className="w-4 h-4 ml-2" />
                          </>
                        ) : ( 
                          <>
                            <span className="hidden sm:inline">
                              {t('browse.buttons.seeMorePlans')} 
                              ({filteredPlans.length - 6} {t('browse.buttons.more')})
                              </span>
                            <span className="sm:hidden">
                              +{filteredPlans.length - 6} 
                              {t('browse.buttons.more')}
                              </span>
                            <ChevronDown className="w-4 h-4 ml-2" />
                          </>
                        )}
                      </button>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>

          {/* Bottom Purchase Section */}
          {selectedPlan && (
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 md:p-4 shadow-lg z-50">
              <div className="max-w-7xl mx-auto">
                {/* Mobile Layout - Stacked */}
                <div className="flex flex-col sm:hidden space-y-3">
                  <div className="flex items-center space-x-3">
                    <Globe className="w-6 h-6 text-blue-600 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-gray-900 text-sm truncate">
                        {selectedCountry.name} eSIM
                      </div>
                      <div className="text-xs text-gray-600 truncate">
                        {selectedPlan.isUnlimited ? t('browse.plans.unlimited') : `${(selectedPlan.data_amount / 1000).toFixed(1)} GB`} • {selectedPlan.duration} {selectedPlan.duration === 1 ? t('browse.plans.day') : t('browse.plans.days')}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-xs text-gray-600">{t('browse.checkout.total')}</div>
                      <div className="text-lg font-bold text-orange-500">
                        USD {selectedPlan.finalPrice.toFixed(2)}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      // Navigate to checkout page
                      window.location.href = `/checkout?plan=${encodeURIComponent(JSON.stringify({
                        id: selectedPlan.bundle_name || selectedPlan.name,
                        country: selectedCountry.name,
                        countryCode: selectedCountry.iso,
                        countryFlag: selectedCountry.image_url,
                        dataAmount: selectedPlan.isUnlimited ? t('browse.plans.unlimited') : `${(selectedPlan.data_amount / 1000).toFixed(1)} GB`,
                        duration: selectedPlan.duration,
                        price: selectedPlan.finalPrice.toFixed(2),
                        planData: selectedPlan
                      }))}`
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors w-full"
                  >
                    {t('browse.buttons.buyNow')}
                  </button>
                </div>

                {/* Desktop Layout - Side by side */}
                <div className="hidden sm:flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <Globe className="w-8 h-8 text-blue-600" />
                    <div>
                      <div className="font-semibold text-gray-900">
                        {selectedCountry.name} eSIM
                      </div>
                      <div className="text-sm text-gray-600">
                        {selectedPlan.isUnlimited ? t('browse.plans.unlimited') : `${(selectedPlan.data_amount / 1000).toFixed(1)} GB`} • {selectedPlan.duration} {selectedPlan.duration === 1 ? t('browse.plans.day') : t('browse.plans.days')}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-6">
                    <div className="text-right">
                      <div className="text-sm text-gray-600">{t('browse.checkout.total')}</div>
                      <div className="text-xl font-bold text-orange-500">
                        USD {selectedPlan.finalPrice.toFixed(2)}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        // Navigate to checkout page
                        window.location.href = `/checkout?plan=${encodeURIComponent(JSON.stringify({
                          id: selectedPlan.bundle_name || selectedPlan.name,
                          country: selectedCountry.name,
                          countryCode: selectedCountry.iso,
                          countryFlag: selectedCountry.image_url,
                          dataAmount: selectedPlan.isUnlimited ? t('browse.plans.unlimited') : `${(selectedPlan.data_amount / 1000).toFixed(1)} GB`,
                          duration: selectedPlan.duration,
                          price: selectedPlan.finalPrice.toFixed(2),
                          planData: selectedPlan
                        }))}`
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-semibold transition-colors"
                    >
                      {t('browse.buttons.buyNow')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>

    {/* Plan Details Modal */}
    {showPlanDetailsModal && <PlanDetailsModal />}
  </div>
);
};

export default Browse;